<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { deleteScan, listScans, updateScan } from '../../engine/history/db';
import type { ScanRecord } from '../../engine/types';
import { useBodyScrollLock } from '../composables/useScrollLock';
import LangPicker from './LangPicker.vue';

const props = withDefaults(defineProps<{ initialFilter?: 'all' | 'fav' }>(), {
  initialFilter: 'all',
});
const emit = defineEmits<{ close: []; open: [payload: { plate: string; region?: string }] }>();

function openRecord(r: ScanRecord): void {
  emit('open', { plate: r.text, ...(r.region ? { region: r.region } : {}) });
}

// The panel scrolls internally; the page behind it must not (phone touch
// scrolls were falling through to the details view).
useBodyScrollLock();

const { locale } = useI18n();
const records = ref<ScanRecord[]>([]);
const filter = ref<'all' | 'fav'>(props.initialFilter);
const swipeX = ref<Record<number, number>>({});
let touchStartX = 0;
let touchId: number | null = null;

const visible = computed(() =>
  filter.value === 'fav' ? records.value.filter((r) => r.favorite) : records.value,
);

onMounted(refresh);

async function refresh(): Promise<void> {
  records.value = await listScans();
}

async function remove(id: number | undefined): Promise<void> {
  if (id === undefined) return;
  await deleteScan(id);
  await refresh();
}

async function toggleFavorite(r: ScanRecord): Promise<void> {
  r.favorite = !r.favorite;
  await updateScan({ ...r });
}

function onTouchStart(e: TouchEvent, id: number | undefined): void {
  if (id === undefined) return;
  touchStartX = e.touches[0]?.clientX ?? 0;
  touchId = id;
}

function onTouchMove(e: TouchEvent): void {
  if (touchId === null) return;
  const dx = (e.touches[0]?.clientX ?? 0) - touchStartX;
  swipeX.value = { [touchId]: Math.min(0, dx) };
}

async function onTouchEnd(): Promise<void> {
  if (touchId === null) return;
  const dx = swipeX.value[touchId] ?? 0;
  if (dx < -90) await remove(touchId); // swipe-left to delete
  swipeX.value = {};
  touchId = null;
}

function fmt(ts: number): string {
  return new Date(ts).toLocaleString(locale.value, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
</script>

<template>
  <div class="panel-backdrop" @click.self="$emit('close')">
    <div class="history-panel">
      <div class="panel-head">
        <h2>{{ $t('history.title') }}</h2>
        <div class="panel-actions">
          <LangPicker />
          <button class="icon-btn" :aria-label="$t('aria.close')" @click="$emit('close')">✕</button>
        </div>
      </div>
      <div class="mode-toggle history-filter" role="tablist">
        <button class="seg" :class="{ active: filter === 'all' }" @click="filter = 'all'">
          {{ $t('history.all') }}
        </button>
        <button class="seg" :class="{ active: filter === 'fav' }" @click="filter = 'fav'">
          ★ {{ $t('history.favorites') }}
        </button>
      </div>
      <p v-if="visible.length === 0" class="muted center-text">
        {{ $t(filter === 'fav' ? 'history.emptyFavorites' : 'history.empty') }}
      </p>
      <ul class="history-list">
        <li
          v-for="r in visible"
          :key="r.id"
          class="history-item"
          :style="{ transform: `translateX(${swipeX[r.id ?? -1] ?? 0}px)` }"
          @touchstart="onTouchStart($event, r.id)"
          @touchmove="onTouchMove($event)"
          @touchend="onTouchEnd()"
        >
          <button
            class="icon-btn star"
            :class="{ on: r.favorite }"
            :aria-label="$t('aria.favorite')"
            @click="toggleFavorite(r)"
          >
            {{ r.favorite ? '★' : '☆' }}
          </button>
          <button class="history-main" type="button" @click="openRecord(r)">
            <div class="plate-small">{{ r.text }}</div>
            <div class="muted tiny">
              {{ fmt(r.ts) }}
              <span v-if="r.region"> · {{ r.region }}</span>
              <span v-if="r.edited"> · {{ $t('history.edited') }}</span>
            </div>
          </button>
          <button class="icon-btn dim" :aria-label="$t('aria.delete')" @click="remove(r.id)">
            ✕
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>
